import { useMutation } from '@apollo/client'
import { useUser } from '@fs/zion-user'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { v4 as uuid } from 'uuid'
import { useOverlayContext } from '../../../../shared/providers/overlayContextProvider/OverlayContext'
import transl from './useCoverages.transl'
import useGroupData from './useGroupData'
import useGroupDataUpdate from './useGroupDataUpdate'
import useGroupWorkflowActions from './useGroupWorkflowActions'
import {
  updateAuditMissingCoveragesAsCompleted,
  getCoveragesAfterDelete,
  handleAuditMissingCoverages,
  getCoverageErrors,
  getEmptyCoverage,
  getEmptyRecordType,
  findRecordType,
  copyCoverages,
  updateApplyToChildren,
  updatePlaceCheckbox,
} from './useCoverages.utils'

const useCoverages = ({ forVolumeSet = false } = {}) => {
  const { group, allRecordTypes, groupCoverageCheckboxes, initialGroup, volumeSetCoverageCheckboxes } = useGroupData()
  const groupCoverages = group?.coverages || []
  const { data: WFAData, error: WFAError } = useGroupWorkflowActions()
  const [markAuditMissingCoverageCompleted] = useMutation(updateAuditMissingCoveragesAsCompleted())

  const { cisId } = useUser()
  const { subscribe } = useOverlayContext()

  const [coverageDataMissing, setCoverageDataMissing] = useState(null)
  const [savingMissingAuditData, setSavingMissingAuditData] = useState(false)
  const [updatedAuditCoverages, setUpdatedAuditCoverages] = useState(false)

  useEffect(() => {
    let isMounted = true
    if (isMounted) {
      const actions = WFAData?.groups?.[0]?.currentWorkflowActions || []
      if (WFAError) {
        setCoverageDataMissing(null)
      } else if (actions.length > 0 && !coverageDataMissing && !updatedAuditCoverages) {
        const coverageDataAction =
          actions.find(({ name, status }) => name === 'AuditMissingCoverage' && status === 'Available') || null
        const properties = coverageDataAction?.properties || []
        const coverageIssue = properties.find(({ name }) => name === 'coverageIssues')
        setCoverageDataMissing(coverageIssue?.value || null)
      }
    }

    return () => {
      isMounted = false
    }
  }, [WFAData, WFAError, coverageDataMissing, updatedAuditCoverages])

  const dispatch = useGroupDataUpdate()
  const coverageCheckboxes = forVolumeSet ? volumeSetCoverageCheckboxes : groupCoverageCheckboxes
  const coverages = forVolumeSet ? initialGroup?.coverages : groupCoverages

  const setAuditMissingCoveragesAsCompleted = useCallback(
    async (statusOverlay) => {
      if (!group?.id) return

      setSavingMissingAuditData(true)

      await handleAuditMissingCoverages({
        groupId: group?.id,
        cisId,
        markAuditMissingCoverageCompleted,
        statusOverlay,
        onSuccess: () => {
          setUpdatedAuditCoverages(true)
          setCoverageDataMissing(null)
        },
        onError: () => {
          // optional extra error handling
        },
      })

      setSavingMissingAuditData(false)
    },
    [group, cisId, markAuditMissingCoverageCompleted]
  )

  const setCoverages = useCallback(
    (updatedCoverages = []) => {
      subscribe(() => 'editPanelOpen')
      dispatch({ forVolumeSet, type: 'SET_COVERAGES', value: updatedCoverages })
    },
    [dispatch, forVolumeSet, subscribe]
  )

  const setCoverageCheckboxes = useCallback(
    (newCoveragesCheckboxMap) => {
      dispatch({ forVolumeSet, type: 'SET_COVERAGE_CHECKBOXES', value: newCoveragesCheckboxMap })
    },
    [dispatch, forVolumeSet]
  )

  const setAllCoverageCheckboxes = useCallback(
    (checked) => {
      dispatch({ forVolumeSet, type: 'UPDATE_ALL_COVERAGE_CHECKBOXES', value: checked })
    },
    [dispatch, forVolumeSet]
  )

  const setApplyToChildren = useCallback(
    (placeIndex, recordTypeIndex, applyToChildren) => {
      const updated = updateApplyToChildren(coverages, placeIndex, recordTypeIndex, applyToChildren)
      setCoverages(updated)
    },
    [coverages, setCoverages]
  )

  const handlePlaceCheckboxChange = useCallback(
    (placeIndex, newValue) => {
      const updated = updatePlaceCheckbox(coverageCheckboxes, placeIndex, newValue)
      setCoverageCheckboxes(updated)
    },
    [coverageCheckboxes, setCoverageCheckboxes]
  )

  const handleRecordTypeCheckboxChange = useCallback(
    (placeIndex, recordTypeIndex, newValue) => {
      const newState = [...coverageCheckboxes]
      const originalRecordTypeCheckboxObj = coverageCheckboxes[placeIndex][recordTypeIndex]

      // set record type checkbox values
      const newRecordTypeCheckedObj = {}
      newRecordTypeCheckedObj.recordTypeChecked = newValue
      // set date checkbox values
      newRecordTypeCheckedObj.datesChecked = originalRecordTypeCheckboxObj.datesChecked.map(() => newValue)

      newState[placeIndex][recordTypeIndex] = newRecordTypeCheckedObj

      setCoverageCheckboxes(newState)
    },
    [coverageCheckboxes, setCoverageCheckboxes]
  )

  const handleDateCheckboxChange = useCallback(
    (placeIndex, recordTypeIndex, dateIndex, newValue) => {
      const newState = [...coverageCheckboxes]
      // set date checkbox values
      const newDateCheckedArray = [...coverageCheckboxes[placeIndex][recordTypeIndex].datesChecked]
      newDateCheckedArray[dateIndex] = newValue
      newState[placeIndex][recordTypeIndex].datesChecked = newDateCheckedArray

      setCoverageCheckboxes(newState)
    },
    [coverageCheckboxes, setCoverageCheckboxes]
  )

  const deleteSelectedCoverageCheckboxes = useCallback(() => {
    const updatedCoverage = getCoveragesAfterDelete(coverages, coverageCheckboxes)
    setCoverages(updatedCoverage)
  }, [coverages, setCoverages, coverageCheckboxes])

  const addPlace = useCallback(() => {
    const coveragesCopy = copyCoverages(coverages)
    setCoverages([...coveragesCopy, { ...getEmptyCoverage() }])
  }, [coverages, setCoverages])

  const setPlace = useCallback(
    (coverageIndex, placeName, placeId, published, fromYear, toYear) => {
      const coveragesCopy = copyCoverages(coverages)
      const coverageToUpdate = coveragesCopy[coverageIndex]
      if (coverageToUpdate) {
        coverageToUpdate.place = {
          fullName: placeName,
          id: String(placeId),
          published,
          ...(fromYear != null && { fromYear }),
          ...(toYear != null && { toYear }),
        }
        if (String(placeId) === coveragesCopy[coverageIndex].id)
          coverageToUpdate.place.name = coveragesCopy[coverageIndex].name
        else coverageToUpdate.place.name = placeName.split(',')[0]
      }

      setCoverages(coveragesCopy)
    },
    [coverages, setCoverages]
  )

  const setDate = useCallback(
    (coverageId, recordTypeId, dates) => {
      const coveragesCopy = copyCoverages(coverages)
      const updatedRecordType = findRecordType(coveragesCopy, coverageId, recordTypeId)
      updatedRecordType.date = dates.map((date) => {
        // Ensure each date has a localKey
        return {
          ...date.value,
          formatted: date.label,
          localKey: date.localKey || uuid(),
        }
      })
      setCoverages(coveragesCopy)
    },
    [coverages, setCoverages]
  )

  const addRecordType = useCallback(
    (coverageId) => {
      const coveragesCopy = copyCoverages(coverages)
      const updatedCoverage = coveragesCopy.find((coverage) => Number(coverage.place.id) === Number(coverageId))
      if (updatedCoverage) {
        updatedCoverage.recordTypes.push(getEmptyRecordType())
      }
      setCoverages(coveragesCopy)
    },
    [coverages, setCoverages]
  )

  const setRecordType = useCallback(
    (placeId, recordTypeId, recordType) => {
      const coveragesCopy = copyCoverages(coverages)
      const updatedRecordType = findRecordType(coveragesCopy, placeId, recordTypeId)
      updatedRecordType.id = recordType.id
      updatedRecordType.name = recordType.name
      setCoverages(coveragesCopy)
    },
    [coverages, setCoverages]
  )

  const coverageErrors = useMemo(() => {
    return getCoverageErrors(coverages)
  }, [coverages])

  const validateCoverages = useCallback(() => {
    if (coverages.length === 0) {
      return transl.placeRequiredMessage()
    }

    const error = coverageErrors.find(
      (err) => err.placeError || err.recordTypeErrors?.find((el) => !!el) || err.dateErrors?.find((el) => !!el)
    )
    return error?.placeError || error?.recordTypeErrors?.find((el) => !!el) || error?.dateErrors?.find((el) => !!el)
  }, [coverages, coverageErrors])

  const validateRecordType = useCallback(
    (placeIndex, recordTypeIndex) => {
      const recordTypeErrors = coverageErrors[placeIndex]?.recordTypeErrors
      return recordTypeErrors?.[recordTypeIndex]
    },
    [coverageErrors]
  )

  const validateDate = useCallback(
    (placeIndex, recordTypeIndex) => {
      const dateErrors = coverageErrors[placeIndex]?.dateErrors
      return dateErrors?.[recordTypeIndex]
    },
    [coverageErrors]
  )

  const validatePlace = useCallback(
    (placeIndex) => {
      return coverageErrors[placeIndex]?.placeError
    },
    [coverageErrors]
  )

  return {
    addPlace,
    addRecordType,
    allRecordTypes,
    coverages,
    coverageCheckboxes,
    coverageDataMissing,
    deleteSelectedCoverageCheckboxes,
    handleDateCheckboxChange,
    handlePlaceCheckboxChange,
    handleRecordTypeCheckboxChange,
    savingMissingAuditData,
    setAllCoverageCheckboxes,
    setApplyToChildren,
    setAuditMissingCoveragesAsCompleted,
    setDate,
    setPlace,
    setRecordType,
    updatedAuditCoverages,
    validateCoverages,
    validatePlace,
    validateRecordType,
    validateDate,
  }
}

export default useCoverages
